"use client"

import { useRef, useState } from "react"
import { Loader2, Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { fetchWithTrace } from "@/lib/fetch-with-trace"
import { Logger } from "@/lib/logger-client"

export interface ResumeInputValue {
  // Retained on the type to avoid touching every caller; UI no longer
  // supports pasting text, so this is always null in practice.
  text: string | null
  resumeId: string | null
  filename: string | null
}

interface ResumeInputProps {
  value: ResumeInputValue
  onChange: (next: ResumeInputValue) => void
  disabled?: boolean
}

export function ResumeInput({ value, onChange, disabled }: ResumeInputProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  async function handleUpload(file: File) {
    setUploading(true)
    setError(null)
    try {
      const form = new FormData()
      form.set("file", file)
      const { response: res } = await fetchWithTrace("/api/resumes", {
        method: "POST",
        credentials: "include",
        body: form,
        logger: Logger(),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(body.error || `Upload failed (HTTP ${res.status})`)
        return
      }
      onChange({
        text: null,
        resumeId: body.resume?.id ?? null,
        filename: body.resume?.name ?? file.name,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed")
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  return (
    <div className="rounded-xl border border-border bg-background p-4">
      {value.resumeId && value.filename ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
          <div className="truncate text-sm">
            <span className="font-medium">{value.filename}</span>
            <span className="ml-2 text-xs text-muted-foreground">uploaded</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || uploading}
            onClick={() => {
              onChange({ text: null, resumeId: null, filename: null })
            }}
          >
            Replace
          </Button>
        </div>
      ) : (
        <label
          className={cn(
            "flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-8 cursor-pointer hover:bg-muted/40 transition-colors",
            (disabled || uploading) && "pointer-events-none opacity-60"
          )}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Uploading…</span>
            </>
          ) : (
            <>
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Click to choose a PDF (max 10 MB)
              </span>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleUpload(file)
            }}
            disabled={disabled || uploading}
            className="hidden"
          />
        </label>
      )}
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}
